import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircledIcon,
  CrossCircledIcon,
  Cross2Icon,
  ExclamationTriangleIcon,
  InfoCircledIcon
} from '@radix-ui/react-icons';

const NotificationContext = createContext(null);

const ICONS = {
  success: CheckCircledIcon,
  error: CrossCircledIcon,
  warning: ExclamationTriangleIcon,
  info: InfoCircledIcon
};

const DEFAULT_DURATION = {
  success: 5000,
  info: 6000,
  warning: 9000,
  error: 12000
};

/**
 * Turns an axios failure into something worth showing a user.
 * The backend is expected to answer with an 'error' string and an optional 'code';
 * when it does we show its message verbatim instead of a generic "please try again".
 */
export function describeApiError(error, fallback = 'Something went wrong. Please try again.') {
  const status = error?.response?.status;
  const data = error?.response?.data || {};
  const code = data.code || null;
  const serverMessage = typeof data.error === 'string' && data.error.trim() ? data.error.trim() : null;

  let message = serverMessage;
  if (!message) {
    if (error?.code === 'ECONNABORTED') {
      message = 'That took too long and was cancelled. Please try again.';
    } else if (!error?.response) {
      message = 'Could not reach the server. Check your connection and try again.';
    } else if (status === 401) {
      message = 'Your session has expired. Please sign in again.';
    } else if (status === 402 || status === 400) {
      message = 'You do not have enough credits for this.';
    } else if (status === 413) {
      message = 'That file is too large. Please use an image under 10MB.';
    } else if (status === 429) {
      message = 'Too many requests right now. Please wait a moment and try again.';
    } else if (status >= 500) {
      message = 'Our image service is having trouble right now. Please try again in a minute.';
    } else {
      message = fallback;
    }
  }

  // 4xx means the user can usually fix it themselves; 5xx and network failures are on us.
  const severity = status && status >= 400 && status < 500 ? 'warning' : 'error';

  return { message, code, status, severity };
}

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const timers = useRef(new Map());
  const nextId = useRef(0);

  const dismiss = useCallback((id) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setNotifications((current) => current.filter((item) => item.id !== id));
  }, []);

  const notify = useCallback((options) => {
    const severity = options.severity || 'info';
    const id = ++nextId.current;
    const notification = {
      id,
      severity,
      title: options.title || null,
      message: options.message || '',
      detail: options.detail || null,
      actionLabel: options.actionLabel || null,
      onAction: options.onAction || null
    };

    setNotifications((current) => [...current.filter((item) => item.message !== notification.message), notification]);

    const duration = options.duration === undefined ? DEFAULT_DURATION[severity] : options.duration;
    if (duration) {
      timers.current.set(id, setTimeout(() => dismiss(id), duration));
    }

    return id;
  }, [dismiss]);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((timer) => clearTimeout(timer));
      pending.clear();
    };
  }, []);

  const value = useMemo(() => ({
    notify,
    dismiss,
    notifySuccess: (message, options) => notify({ ...options, severity: 'success', message }),
    notifyInfo: (message, options) => notify({ ...options, severity: 'info', message }),
    notifyWarning: (message, options) => notify({ ...options, severity: 'warning', message }),
    notifyError: (message, options) => notify({ ...options, severity: 'error', message }),
    notifyApiError: (error, fallback, options) => {
      const described = describeApiError(error, fallback);
      notify({ ...options, severity: described.severity, message: described.message });
      return described;
    }
  }), [notify, dismiss]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <div className="toast-viewport" role="region" aria-label="Notifications">
        {notifications.map((notification) => {
          const Icon = ICONS[notification.severity] || InfoCircledIcon;
          return (
            <div
              key={notification.id}
              className={`toast toast-${notification.severity}`}
              role={notification.severity === 'error' ? 'alert' : 'status'}
            >
              <Icon className="toast-icon" />
              <div className="toast-body">
                {notification.title && <strong className="toast-title">{notification.title}</strong>}
                <span className="toast-message">{notification.message}</span>
                {notification.detail && <span className="toast-detail">{notification.detail}</span>}
                {notification.actionLabel && notification.onAction && (
                  <button
                    type="button"
                    className="toast-action"
                    onClick={() => {
                      notification.onAction();
                      dismiss(notification.id);
                    }}
                  >
                    {notification.actionLabel}
                  </button>
                )}
              </div>
              <button
                type="button"
                className="toast-close"
                onClick={() => dismiss(notification.id)}
                aria-label="Dismiss notification"
              >
                <Cross2Icon />
              </button>
            </div>
          );
        })}
      </div>
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used inside a NotificationProvider');
  }
  return context;
}

export default NotificationProvider;
